import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  useDroppable,
} from '@dnd-kit/core';
import { VariableSeleccionada, VariableHierarchyNode, EjeVariable } from '../../types';
import { VariableComboSelector } from './VariableComboSelector';

interface EjeVariablesProps {
  eje: EjeVariable;
  variables: VariableSeleccionada[];
  allVariables: VariableSeleccionada[];
  hierarchy: VariableHierarchyNode[];
  onVariableMove: (variableId: string, sensorId: string, newEje: EjeVariable) => void;
  onVariableRemove: (variableId: string, sensorId: string) => void;
  onVariableAdd: (variableId: string, sensorId: string, sensorNombre: string | null, eje: EjeVariable) => void;
  color: string;
  label: string;
  colorMap?: Record<string, string>;
}

export const EjeVariables: React.FC<EjeVariablesProps> = ({
  eje,
  variables,
  allVariables,
  hierarchy,
  onVariableMove,
  onVariableRemove,
  onVariableAdd,
  color,
  label,
  colorMap,
}) => {
  const theme = useTheme();

  const handleVariableSelect = (variableId: string, sensorId: string, sensorNombre: string | null) => {
    const exists = allVariables.some(
      (v) => v.variable_id === variableId && v.sensor_id === sensorId
    );
    
    if (!exists) {
      // Pasar el eje directamente al agregar la variable
      onVariableAdd(variableId, sensorId, sensorNombre, eje);
    } else {
      // Si ya existe, solo moverla al eje correcto
      onVariableMove(variableId, sensorId, eje);
    }
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: alpha(color, 0.05),
        border: `2px solid ${alpha(color, 0.3)}`,
        borderRadius: 2,
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: color,
          }}
        >
          {label}
        </Typography>
        <Chip
          label={variables.length}
          size="small"
          sx={{
            backgroundColor: color,
            color: theme.palette.getContrastText(color),
            fontWeight: 600,
            minWidth: 32,
          }}
        />
      </Box>

      <Box sx={{ mb: 0 }}>
        <DroppableArea eje={eje} color={color}>
          <VariableComboSelector
            hierarchy={hierarchy}
            selectedVariables={allVariables}
            onVariableSelect={handleVariableSelect}
            onVariableRemove={onVariableRemove}
            placeholder={`Buscar variable para ${label.toLowerCase()}...`}
            color={color}
            ejeVariables={variables}
            colorMap={colorMap}
          />
        </DroppableArea>
      </Box>
    </Paper>
  );
};

const DroppableArea: React.FC<{ eje: EjeVariable; color: string; children: React.ReactNode }> = ({
  eje,
  color,
  children,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `eje-${eje}`,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(isOver && {
          '& > *': {
            backgroundColor: alpha(color, 0.15),
            border: `2px dashed ${color} !important`,
          },
        }),
      }}
    >
      {children}
    </Box>
  );
};
